import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NdfDemandsCalculationsOperation } from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { NdfOperationFilteredSummary } from '@interfaces/a-organizar/ndfOperationsFilteredSummary';
import { PTaxQuotation } from '@interfaces/a-organizar/pTaxQuotation';
import { Option } from '@interfaces/common/option';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { TranslateService } from '@ngx-translate/core';
import { Checkbox } from '@src/app/core/interfaces/a-organizar/checkbox';
import { IdValue } from '@src/app/core/interfaces/a-organizar/idValue';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { debounceTime, Subject } from 'rxjs';
import { CardOperationSummaryAction } from '@features/dashboard/control/derivative-management/derivatives-old-version/ndf-operation-list/ndf-operation-card-summary/ndf-operation-card-summary.component';
import { LiquidationType } from '@features/dashboard/control/derivative-management/derivatives/components/derivative-form-item/mixins/derivative-ellipsis-menu.mixin';

/** Resumo exibido no topo da modal: vem da listagem antiga (modo externo) ou do GET da operação. */
export type LiquidationOperationSummary =
  | NdfOperationFilteredSummary
  | NdfDemandsCalculationsOperation;

export interface LiquidationRequestPayload {
  operationId: string;
  settlementDate: string;
  settlementNotional: number;
  settlementRate: number;
  settlementSpot: number;
  totalAmount?: number;
  withholdingTax?: number;
  incomeTaxPayable?: number;
  netAmount?: number;
  isManuallyAdjusted?: boolean;
  manualTotalAmount?: number;
  manualWithholdingTax?: number;
  manualIncomeTaxPayable?: number;
}

@Component({
  selector: 'app-modal-liquidate-operation',
  templateUrl: './modal-liquidate-operation.component.html',
  styleUrls: ['./modal-liquidate-operation.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule],
})
export class ModalLiquidateOperationComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef!: DialogComponent;

  @Input() operationId: string | null = null;
  @Input() operationToSaleSummary: LiquidationOperationSummary | null = null;
  @Input() operationToSale: NdfDemandsCalculationsOperation | null = null;
  @Input() operationSaleForm: FormGroup | null = null;
  @Input() operationSaleProps: Record<string, string> = {};
  @Input() ptaxQuotesDates: PTaxQuotation[] | null = null;
  @Input() isMobile = false;
  @Input() currencyOptions: Option[] = [];
  @Input() currentDate: Date = new Date();
  @Input() liquidationType: LiquidationType | '' = '';
  @Input() ptaxQuotationDate: string | null = null;
  @Input() showSaveReversalButton = true;

  @Output() sendOperationCardSummaryAction = new EventEmitter<CardOperationSummaryAction>();
  @Output() saveReversalRequested = new EventEmitter<LiquidationRequestPayload>();
  @Output() calendarValue = new EventEmitter<{ value: string[]; field: string }>();
  @Output() inputNumber = new EventEmitter<{ value: IdValue; field: string }>();
  @Output() liquidateRequested = new EventEmitter<LiquidationRequestPayload>();

  /** Exibe o spinner do projeto enquanto a modal busca operação/PTAX/simulação (modo interno).
   *  Nome próprio (não `isLoading`) para não colidir com o membro da base VaultBaseComponent. */
  isModalLoading = false;
  /** Contador de requisições em voo: o spinner some só quando todas terminam (op + PTAX + simulação). */
  private pendingRequests = 0;
  /** Há uma simulação enfileirada/em andamento (inclui a janela do debounce): os totais
   *  Ajuste Bruto/IRRF/Líquido ainda NÃO estão prontos. Usado para bloquear a submissão. */
  pendingSimulation = false;
  /** Token de sequência da simulação: só a resposta da ÚLTIMA requisição aplica os totais e libera o
   *  gate. Respostas defasadas/fora de ordem (requisições anteriores ainda em voo) são ignoradas, para
   *  não destravar a submissão com totais antigos. */
  private latestSimulationSeq = 0;
  /** A última simulação falhou (erro ou resposta vazia): os totais exibidos NÃO refletem os inputs
   *  atuais, então a submissão fica bloqueada até uma simulação bem-sucedida (ex.: ajustar um campo). */
  simulationFailed = false;
  /** Mensagem do backend da última simulação recusada (ex.: data fora do intervalo, dia não útil). Mostrada
   *  no lugar do texto genérico, porque é ela que diz à pessoa o que ajustar. */
  simulationErrorMessage: string | null = null;
  /** Geração do carregamento da operação: incrementada a cada `loadOperationById` (nova operação no
   *  modal). Callbacks assíncronos (carregar operação / PTAX / simulação) capturam a geração e só
   *  aplicam efeitos se ainda for a atual — evita que respostas em voo da operação ANTERIOR vazem
   *  para a operação atual (patch/destrava no modal errado). */
  private loadGeneration = 0;

  /** Bloqueia liquidar/salvar enquanto carrega, há simulação pendente, ou a última simulação falhou
   *  (modo interno) — para não submeter totais defasados. */
  get isSubmitBlocked(): boolean {
    return (
      !this.isExternalMode &&
      (this.isModalLoading ||
        this.pendingSimulation ||
        this.simulationFailed ||
        this.isSettlementRateMissing)
    );
  }

  /** A Taxa de Liquidação ainda não foi informada (modo interno). Sem ela o backend recusa a simulação,
   *  então a modal nem simula: pede a taxa. Vale também no modo manual: o backend exige a taxa para
   *  liquidar mesmo quando o ajuste é digitado. */
  get isSettlementRateMissing(): boolean {
    if (this.isExternalMode || !this.internalOperationToSale) {
      return false;
    }
    const hasDate = !!this.internalOperationSaleForm.get('settlementDate')?.value;
    return hasDate && Number(this.internalOperationSaleForm.get('settlementRate')?.value || 0) <= 0;
  }

  /** Orientação mostrada quando falta a taxa: no vencimento ela vem da PTAX (campo travado); nos demais
   *  modos é a taxa combinada com o banco, digitada pela pessoa. */
  get missingSettlementRateMessage(): string {
    if (this.liquidationType === 'full') {
      return 'Não há PTAX publicada para esta data de liquidação, então a Taxa de Liquidação não pôde ser preenchida. Use a liquidação "Integral antes do vencimento" e informe a taxa combinada com o banco.';
    }
    return 'Informe a Taxa de Liquidação combinada com o banco para calcular o ajuste. A PTAX do dia aparece abaixo do campo só como referência.';
  }

  private readonly simulateSubject = new Subject<void>();
  private internalOperationToSale: NdfDemandsCalculationsOperation | null = null;
  private internalOperationToSaleSummary: NdfDemandsCalculationsOperation | null = null;
  private internalPtaxQuotationDate: string | null = null;
  private readonly internalOperationSaleProps = {
    settlementDate: 'settlementDate',
    settlementNotional: 'settlementNotional',
    settlementRate: 'settlementRate',
    settlementSpot: 'settlementSpot',
    totalAmount: 'totalAmount',
    withholdingTax: 'withholdingTax',
    incomeTaxPayable: 'incomeTaxPayable',
    netAmount: 'netAmount',
    isManuallyAdjusted: 'isManuallyAdjusted',
  };
  private readonly internalOperationSaleForm = this.formBuilder.group({
    settlementDate: ['', [Validators.required]],
    settlementNotional: [0, [Validators.required, Validators.min(0)]],
    // null (não 0) quando não há taxa: o campo aparece vazio, com o placeholder, em vez de "0,0000"
    // (ou "0,1000", que o min=0.1 do p-inputNumber exibia e escondia o 0 enviado).
    settlementRate: [null as number | null, [Validators.required, Validators.min(0.0001)]],
    settlementSpot: [0],
    totalAmount: [0],
    withholdingTax: [0],
    incomeTaxPayable: [0],
    netAmount: [0],
    isManuallyAdjusted: [false],
  });

  /** Definição do checkbox de ajuste manual, no padrão do app-checkbox do projeto. */
  get manualAdjustmentCheckbox(): Checkbox {
    return {
      label: 'Valores editados manualmente',
      name: 'isManuallyAdjusted',
      value: 'true',
      isChecked: this.isManualAdjustment,
      isDisabled: false,
    };
  }

  /** Modo "valores editados manualmente": o usuário digita ajuste bruto, IRRF e IR a pagar. */
  get isManualAdjustment(): boolean {
    return !!this.currentOperationSaleForm?.get(
      this.currentOperationSaleProps['isManuallyAdjusted'] || 'isManuallyAdjusted'
    )?.value;
  }

  get isExternalMode(): boolean {
    return !!this.operationToSale && !!this.operationSaleForm;
  }

  get currentOperationToSale(): NdfDemandsCalculationsOperation | null {
    return this.isExternalMode ? this.operationToSale : this.internalOperationToSale;
  }

  get currentOperationToSaleSummary(): LiquidationOperationSummary | null {
    return this.isExternalMode ? this.operationToSaleSummary : this.internalOperationToSaleSummary;
  }

  get currentOperationSaleForm(): FormGroup {
    // isExternalMode já garante operationSaleForm preenchido; o `&&` só deixa isso explícito para o TS.
    return this.isExternalMode && this.operationSaleForm
      ? this.operationSaleForm
      : this.internalOperationSaleForm;
  }

  get currentOperationSaleProps(): Record<string, string> {
    return this.isExternalMode ? this.operationSaleProps : this.internalOperationSaleProps;
  }

  get currentPtaxQuotationDate(): string | null {
    return this.isExternalMode ? this.ptaxQuotationDate : this.internalPtaxQuotationDate;
  }

  getOperationStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'Simulated':
        return 'Simulado';
      case 'Quoted':
        return 'Cotado';
      case 'Hired':
        return 'Contratado';
      case 'Settled':
        return 'Liquidado';
      case 'Reversed':
        return 'Revertido';
      default:
        return status || '-';
    }
  }

  getFinancialStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'Open':
        return 'Em Aberto';
      case 'Settled':
        return 'Liquidado';
      case 'Reversed':
        return 'Revertido';
      default:
        return status || '-';
    }
  }

  getCompositionLabel(composition: string | undefined): string {
    switch (composition) {
      case 'Full':
        return 'Integral';
      case 'Partial':
        return 'Parcial';
      default:
        return composition || '-';
    }
  }

  constructor(
    translateService: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly ndfCalculatorService: NdfCalculatorService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(translateService);
    this.simulateSubject.pipe(debounceTime(400)).subscribe(() => {
      this.simulateInternalSaleOperation();
    });
  }

  open(operationId?: string, liquidationType?: LiquidationType): void {
    const targetId = operationId || this.operationId;
    if (liquidationType) {
      this.liquidationType = liquidationType;
    } else if (!this.liquidationType) {
      this.liquidationType = 'partial-before';
    }

    if (!this.isExternalMode && targetId) {
      this.internalPtaxQuotationDate = null;
      this.pendingRequests = 0;
      this.isModalLoading = false;
      this.loadOperationById(targetId);
    }

    this.dialogRef?.open(
      '',
      '',
      '',
      this.t(this.tk.General.Cancel) || '',
      '',
      'sale-operation',
      false,
      false
    );
  }

  close(): void {
    this.dialogRef?.close();
  }

  onCalendarValue(value: unknown): void {
    const normalizedDateArray = this.normalizeCalendarValue(value);
    if (this.isExternalMode) {
      this.calendarValue.emit({ value: normalizedDateArray, field: 'settlementDate' });
      return;
    }

    const rawValue = normalizedDateArray[0] || '';
    const dateValue =
      typeof rawValue === 'string' ? rawValue.split('T')?.[0] || rawValue || '' : '';
    this.internalOperationSaleForm.patchValue({ settlementDate: dateValue });
    this.updateInternalPtaxQuotationDate(dateValue, false);
    this.enqueueSimulation();
  }

  onInputNumber(value: unknown, field: string): void {
    const parsedValue = this.extractNumericValue(value);
    if (this.isExternalMode) {
      this.inputNumber.emit({ value: { value: parsedValue } as IdValue, field });
      return;
    }

    // Taxa apagada vira null, não 0: senão o min=0.1 do input volta a exibir "0,1000".
    const formValue = field === 'settlementRate' && !(parsedValue > 0) ? null : parsedValue;
    this.internalOperationSaleForm.patchValue({ [field]: formValue });
    this.enqueueSimulation();
  }

  /**
   * Liga/desliga o modo manual. Ao ligar, a simulação em voo é DESCARTADA (o token avança) e o gate de
   * submissão é liberado: nada pode sobrescrever o que a pessoa acabou de digitar. Ao desligar, volta a
   * simular para trazer o cálculo do motor.
   */
  onManualAdjustmentToggle(checkbox: Checkbox): void {
    const checked = !!checkbox?.isChecked;
    this.currentOperationSaleForm?.patchValue(
      { isManuallyAdjusted: checked },
      { emitEvent: false }
    );

    if (checked) {
      this.latestSimulationSeq++;
      this.pendingSimulation = false;
      this.simulationFailed = false;
      this.recalculateNetAmount();
    } else {
      this.enqueueSimulation();
    }
    this.cdr.markForCheck();
  }

  /** Digitação nos campos manuais: não simula, só refaz o líquido para o usuário ver o efeito. */
  onManualValueInput(value: unknown, field: string): void {
    this.currentOperationSaleForm?.patchValue({ [field]: this.extractNumericValue(value) });
    this.recalculateNetAmount();
  }

  /** Ajuste Líquido é sempre derivado — nunca digitado. */
  private recalculateNetAmount(): void {
    const form = this.currentOperationSaleForm;
    if (!form) return;

    const netAmount =
      Number(form.get('totalAmount')?.value || 0) -
      Number(form.get('withholdingTax')?.value || 0) -
      Number(form.get('incomeTaxPayable')?.value || 0);

    form.patchValue({ netAmount }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private normalizeCalendarValue(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map(item => (typeof item === 'string' ? item : '')).filter(item => item !== '');
    }

    if (typeof value === 'string' && value) {
      return [value];
    }

    return [''];
  }

  private extractNumericValue(value: unknown): number {
    if (typeof value === 'number') {
      return value;
    }

    if (value && typeof value === 'object' && 'value' in value) {
      return Number((value as { value?: unknown }).value ?? 0);
    }

    return Number(value || 0);
  }

  /** Enfileira uma simulação marcando-a como pendente (cobre a janela do debounce até os totais ficarem
   *  prontos), evitando que a submissão use Ajuste/IRRF/Líquido defasados. */
  private enqueueSimulation(): void {
    // Em modo manual não existe simulação: os valores são do usuário, e uma resposta em voo os
    // sobrescreveria.
    if (this.isManualAdjustment) {
      this.pendingSimulation = false;
      this.simulationFailed = false;
      this.cdr.markForCheck();
      return;
    }

    // Avança o token JÁ no enfileiramento (mudança de input), não no envio: assim uma requisição
    // anterior ainda em voo durante a janela do debounce fica defasada (token capturado < atual) e sua
    // resposta é ignorada — não pode aplicar totais antigos nem destravar a submissão.
    this.latestSimulationSeq++;
    this.pendingSimulation = true;
    // Nova tentativa: limpa o estado de falha anterior.
    this.simulationFailed = false;
    this.simulationErrorMessage = null;
    this.cdr.markForCheck();
    this.simulateSubject.next();
  }

  onSaveReversalClick(): void {
    if (this.isSubmitBlocked) return;
    const payload = this.buildPayload();
    if (!payload) return;
    this.saveReversalRequested.emit(payload);
  }

  onLiquidateClick(): void {
    if (this.isSubmitBlocked) return;
    const payload = this.buildPayload();
    if (!payload) return;
    this.liquidateRequested.emit(payload);
  }

  private buildPayload(): LiquidationRequestPayload | null {
    const operationId = this.currentOperationToSale?.id;
    const form = this.currentOperationSaleForm;
    const props = this.currentOperationSaleProps;
    if (!operationId || !form) {
      return null;
    }

    const settlementDateControl = form.get(props['settlementDate']);
    const settlementRateControl = form.get(props['settlementRate']);
    settlementDateControl?.markAsTouched();
    settlementRateControl?.markAsTouched();
    settlementDateControl?.updateValueAndValidity();
    settlementRateControl?.updateValueAndValidity();

    if (settlementDateControl?.invalid || settlementRateControl?.invalid) {
      return null;
    }

    const isManual = this.isManualAdjustment;

    return {
      operationId,
      settlementDate: String(form.get(props['settlementDate'])?.value || ''),
      settlementNotional: Number(form.get(props['settlementNotional'])?.value || 0),
      settlementRate: Number(form.get(props['settlementRate'])?.value || 0),
      settlementSpot: Number(form.get(props['settlementSpot'])?.value || 0),
      totalAmount: Number(form.get(props['totalAmount'])?.value || 0),
      withholdingTax: Number(form.get(props['withholdingTax'])?.value || 0),
      incomeTaxPayable: Number(form.get(props['incomeTaxPayable'])?.value || 0),
      netAmount: Number(form.get(props['netAmount'])?.value || 0),
      isManuallyAdjusted: isManual,
      manualTotalAmount: isManual ? Number(form.get(props['totalAmount'])?.value || 0) : undefined,
      manualWithholdingTax: isManual
        ? Number(form.get(props['withholdingTax'])?.value || 0)
        : undefined,
      manualIncomeTaxPayable: isManual
        ? Number(form.get(props['incomeTaxPayable'])?.value || 0)
        : undefined,
    };
  }

  /** Marca o início de uma requisição em voo e liga o spinner. */
  private beginLoading(): void {
    this.pendingRequests++;
    this.isModalLoading = true;
    // markForCheck: as atualizações vêm de callbacks assíncronos (HTTP); sob OnPush a view só
    // re-renderizava no próximo clique. Marca o componente para CD ao ligar/desligar o estado.
    this.cdr.markForCheck();
  }

  /** Marca o término de uma requisição; desliga o spinner só quando não há mais nada em voo.
   *  Como TODO callback assíncrono (operação/PTAX/simulação) termina aqui, o markForCheck deste
   *  método também garante que os campos Ajuste Bruto/IRRF/Líquido atualizem sem precisar de clique. */
  private endLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.isModalLoading = this.pendingRequests > 0;
    this.cdr.markForCheck();
  }

  private loadOperationById(operationId: string): void {
    // Nova geração: invalida qualquer callback em voo (operação/PTAX/simulação) da operação anterior.
    const gen = ++this.loadGeneration;
    // Começa bloqueado até a nova operação carregar e simular — não herda o estado liberado/falho da
    // operação anterior.
    this.pendingSimulation = true;
    this.simulationFailed = false;
    this.beginLoading();
    this.ndfCalculatorService.getNdfOperation(operationId).subscribe({
      next: operation => {
        // Outra operação foi aberta no meio do caminho — descarta esta resposta.
        if (gen !== this.loadGeneration) {
          this.endLoading();
          return;
        }
        if (!operation) {
          this.endLoading();
          return;
        }

        this.internalOperationToSale = operation;
        this.internalOperationToSaleSummary = operation;
        this.internalPtaxQuotationDate = null;

        this.internalOperationSaleForm.patchValue({
          settlementDate: operation.settlementDate || '',
          settlementNotional: operation.settlementNotional || operation.notionalAmount || 0,
          settlementRate: operation.settlementRate || null,
          settlementSpot: operation.settlementSpot || 0,
          totalAmount: operation.totalAmount || 0,
          withholdingTax: operation.withholdingTax || 0,
          incomeTaxPayable: operation.incomeTaxPayable || 0,
          netAmount: operation.netAmount || 0,
          isManuallyAdjusted: !!operation.isSettlementManuallyAdjusted,
        });

        this.applyInternalLiquidationTypeDefaults();
        if (this.liquidationType !== 'full') {
          const initialSettlementDate = String(
            this.internalOperationSaleForm.get('settlementDate')?.value || ''
          );
          this.updateInternalPtaxQuotationDate(initialSettlementDate, false);
        }
        this.endLoading();
        // Em modo 'full' a Taxa de Liquidação vem da PTAX (busca assíncrona); a simulação é disparada
        // APÓS a PTAX ser aplicada (dentro de updateInternalPtaxQuotationDate), para não simular com
        // settlementRate=0/desatualizado. Nos demais modos a taxa é do usuário/existente, então simula já.
        if (this.liquidationType !== 'full') {
          this.enqueueSimulation();
        }
      },
      error: () => this.endLoading(),
    });
  }

  private applyInternalLiquidationTypeDefaults(): void {
    if (this.isExternalMode || !this.internalOperationToSale) {
      return;
    }

    const operation = this.internalOperationToSale;
    const dueAt = operation.dueAt || '';
    const notionalAmount = Number(operation.notionalAmount || 0);

    switch (this.liquidationType) {
      case 'full':
        this.internalOperationSaleForm.patchValue({
          settlementDate: dueAt,
          settlementNotional: notionalAmount,
        });
        this.updateInternalPtaxQuotationDate(dueAt, true);
        break;
      case 'partial-before':
        this.internalOperationSaleForm.patchValue({
          settlementDate: '',
          settlementNotional: notionalAmount,
        });
        this.internalPtaxQuotationDate = null;
        break;
      case 'partial':
      default:
        break;
    }
  }

  private updateInternalPtaxQuotationDate(
    settlementDate: string,
    applySettlementRate: boolean
  ): void {
    if (this.isExternalMode || !this.internalOperationToSale) {
      return;
    }

    const normalizedDate = String(settlementDate || '').split('T')[0] || '';
    // A moeda da operação pode estar só na demanda; o backend já resolve e devolve internationalCurrencyId
    // (com fallback). Antes lia-se `internationalCurrency`, que não é o GUID enviado pela API, então a
    // busca de PTAX nunca acontecia e a Taxa de Liquidação ficava 0.
    const internationalCurrencyId = String(
      this.internalOperationToSale.internationalCurrencyId || ''
    );

    if (!normalizedDate || !internationalCurrencyId) {
      this.internalPtaxQuotationDate = null;
      return;
    }

    const gen = this.loadGeneration;
    this.beginLoading();
    this.ndfCalculatorService
      .getNdfPreviousDayPTaxLessHoliday(internationalCurrencyId, normalizedDate)
      .subscribe({
        next: response => {
          // A operação do modal mudou enquanto a PTAX estava em voo — não aplica no modal errado.
          if (gen !== this.loadGeneration) {
            this.endLoading();
            return;
          }
          this.internalPtaxQuotationDate =
            (response?.quoteDate as unknown as string) || normalizedDate || null;

          if (applySettlementRate) {
            this.internalOperationSaleForm.patchValue({
              settlementRate: Number(response?.saleRate || 0) || null,
            });
            // Só agora (com a taxa da PTAX aplicada) dispara a simulação — evita simular com taxa 0.
            this.enqueueSimulation();
          }
          this.endLoading();
        },
        error: () => {
          // Operação trocou durante a PTAX em voo — ignora.
          if (gen !== this.loadGeneration) {
            this.endLoading();
            return;
          }
          this.internalPtaxQuotationDate = normalizedDate || null;
          // Mesmo se a PTAX falhar, dispara a simulação no modo full para não travar o cálculo.
          if (applySettlementRate) {
            this.enqueueSimulation();
          }
          this.endLoading();
        },
      });
  }

  private simulateInternalSaleOperation(): void {
    if (this.isExternalMode || !this.internalOperationToSale?.id) {
      return;
    }

    const settlementDate = this.internalOperationSaleForm.get('settlementDate')?.value;
    if (!settlementDate) {
      // Sem data não há o que simular; libera o gate de submissão.
      this.pendingSimulation = false;
      this.cdr.markForCheck();
      return;
    }

    // Sem taxa o backend recusa ("A Taxa de liquidação deve ser maior que zero") e a pessoa só via a
    // mensagem genérica. Não simula: o template pede a taxa e o envio fica bloqueado
    // (isSettlementRateMissing). Os totais antigos são zerados para não parecerem calculados.
    if (Number(this.internalOperationSaleForm.get('settlementRate')?.value || 0) <= 0) {
      this.internalOperationSaleForm.patchValue(
        { totalAmount: 0, withholdingTax: 0, incomeTaxPayable: 0, netAmount: 0 },
        { emitEvent: false }
      );
      this.pendingSimulation = false;
      this.simulationFailed = false;
      this.cdr.markForCheck();
      return;
    }

    this.beginLoading();
    // Captura o token vigente (avançado no enqueue) E a geração da operação atual. Se um novo enqueue
    // ocorrer (token avança) ou outra operação for aberta (geração avança) enquanto esta requisição
    // estiver em voo, a resposta passa a ser defasada e é ignorada.
    const requestSeq = this.latestSimulationSeq;
    const gen = this.loadGeneration;
    this.ndfCalculatorService
      .putSaleOperationSimulated(
        this.internalOperationToSale.id,
        String(settlementDate),
        Number(this.internalOperationSaleForm.get('settlementNotional')?.value || 0),
        Number(this.internalOperationSaleForm.get('settlementRate')?.value || 0),
        Number(this.internalOperationSaleForm.get('settlementSpot')?.value || 0)
      )
      .subscribe({
        next: response => {
          // Resposta defasada (simulação mais nova disparada OU operação trocada): não aplica os totais
          // nem libera o gate — quem governa é a última requisição da operação atual. Só equilibra o loading.
          const isLatest = requestSeq === this.latestSimulationSeq && gen === this.loadGeneration;
          if (isLatest) {
            if (response) {
              this.internalOperationSaleForm.patchValue(
                {
                  settlementNotional: response.settlementNotional || 0,
                  settlementRate: response.settlementRate || null,
                  settlementSpot: response.settlementSpot || 0,
                  totalAmount: response.totalAmount || 0,
                  withholdingTax: response.withholdingTax || 0,
                  incomeTaxPayable: response.incomeTaxPayable || 0,
                  netAmount: response.netAmount || 0,
                },
                { emitEvent: false }
              );
              // Totais prontos: libera a submissão.
              this.pendingSimulation = false;
              this.simulationFailed = false;
            } else {
              // Resposta vazia da última requisição: totais não confiáveis → mantém bloqueado.
              this.pendingSimulation = false;
              this.simulationFailed = true;
            }
          }
          this.endLoading();
        },
        error: (error: { errors?: { message?: string }[] } | null) => {
          if (requestSeq === this.latestSimulationSeq && gen === this.loadGeneration) {
            // Falha da última simulação (operação atual): os totais exibidos estão defasados → bloqueia
            // a submissão (pendingSimulation=false tira o spinner, mas simulationFailed mantém o gate).
            this.pendingSimulation = false;
            this.simulationFailed = true;
            // Sem ponto final: o template fecha a frase.
            this.simulationErrorMessage =
              error?.errors?.[0]?.message?.replace(/\.+\s*$/, '') || null;
          }
          this.endLoading();
        },
      });
  }
}
